/**
 * Swap a material — `docs/visit-page.md` §9 (Board 4 A fixed price, B time &
 * materials, C blocked).
 *
 * Swap is easy until it is bought, and the approved quote is never edited
 * (do-not-substitute 7). On a fixed-price (or per-visit) visit the customer's
 * price does not change and the visit pays the difference — shown in money
 * before Swap; on time & materials the invoice follows what is used at the new
 * price. The sheet may ask the salesperson for a change order (a task) — it
 * never makes one.
 */
import { z } from 'zod';
import { MATERIAL_SOURCES, SWAP_REASONS } from './values.js';
// ---------------------------------------------------------------------------
// GET /visits/:id/materials/:line/swap-options
// ---------------------------------------------------------------------------
/**
 * `?itemId=` prices an item found with *＋ Find another item* (answered as
 * `picked`); `?qty=` prices a split's part (*some now, the rest later*).
 */
export const swapOptionsQuerySchema = z.object({
    itemId: z.string().uuid().optional(),
    qty: z.coerce.number().positive().max(1_000_000_000).optional(),
});
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
/**
 * `POST /visits/:id/materials/:line/swap` — `{toItemId, reason, split?,
 * askChangeOrder, tellCrew}` (§5.2), plus *Tell the customer* on T&M (§9.2)
 * and the line's version.
 */
export const swapSchema = z.object({
    version: z.number().int().min(1),
    toItemId: z.string().uuid(),
    /** **Why** (required). */
    reason: z.enum(SWAP_REASONS, { message: 'Say why.' }),
    /** What *Other* means, or a word on it (*Karen OK'd the colour by text*). */
    note: z.string().trim().max(2000).default(''),
    /**
     * *or split it (some now, the rest later)*: `qty` of the line becomes a new
     * line of `toItemId` with its own `source`; the rest stays as it is.
     */
    split: z.object({
        qty: z.number().positive().max(1_000_000_000),
        source: z.enum(MATERIAL_SOURCES),
    }).optional(),
    /** **Ask <salesperson> for a change order** — a task, never a change order (default off, D7). */
    askChangeOrder: z.boolean().default(false),
    /** **Tell the customer** — T&M only; a text through Activity (default on). Ignored on fixed price. */
    tellCustomer: z.boolean().default(true),
    /** **Tell the crew** — a *This visit* crew note: *<old> changed to <new> — <reason>.* (default on). */
    tellCrew: z.boolean().default(true),
});
/**
 * `POST /visits/:id/materials/:line/ask-to-cancel` — Board 4 C's **Ask Kyle
 * Brandt to cancel the line**: a task for the PO's buyer, with the line.
 */
export const askToCancelLineSchema = z.object({
    note: z.string().trim().max(2000).default(''),
});
//# sourceMappingURL=swap.js.map